// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import AddressInfoCondensedDisplay from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import DistanceDisplay, { DistanceUnitOfMeasure } from "@insite/content-library/Components/DistanceDisplay";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LocationContactField from "@stoneage/App/Components/Locations/LocationContactField";
import LocationTypeTags from "@stoneage/App/Components/Locations/LocationTypeTags";
import { AddressInfoCondensedDisplayStyles } from "@stoneage/Overrides/Components/AddressInfoCondensedDisplay";
import { XLocationModel } from "@stoneage/Overrides/Components/LocationSearchForm";
import { fontConfig, STONEAGE_OFFICE_LOCATION_TYPE } from "@stoneage/XDL/constants";
import { color, ellipsis } from "@stoneage/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

interface LocationResultCardStyles {
    searchResultStyledWrapper?: InjectableCss;
    locationNameLink?: LinkPresentationProps;
    locationTypesStylesWrapper?: InjectableCss;
    locationDistanceDisplayText?: TypographyPresentationProps;
    locationAddressInfoCondensed?: AddressInfoCondensedDisplayStyles;
}

interface LocationResultCardProps {
    location: XLocationModel;
    distanceUnitOfMeasure: DistanceUnitOfMeasure;
    onLocationClick: (event: React.MouseEvent<Element, MouseEvent>, location: XLocationModel) => void;
}

export const locationContactFieldStyles: LocationResultCardStyles = {
    searchResultStyledWrapper: {
        css: css`
            margin-bottom: 16px;
            width: 100%;
            background-color: ${color("common.ui3")};
            box-shadow: 0 1px 4px 0 ${color("common.ui1", 0.2)};
            padding: 12px;
            ${({ isStoneAge }: any) => css`
                border-left: 3px solid ${color(isStoneAge ? "common.ui2" : "common.ui20")};
            `};
        `,
    },
    locationNameLink: {
        color: "secondary",
        typographyProps: {
            ...fontConfig.regularSingleLine,
            ellipsis: true,
            weight: fontConfig.weight.medium,
            css: css`
                ${ellipsis(2)}
                white-space: normal;
            `,
        },
    },

    locationTypesStylesWrapper: {
        css: css`
            margin-bottom: 8px;
            font-size: ${fontConfig.small.size};
        `,
    },
    locationDistanceDisplayText: {
        css: css`
            min-height: 23px;
            min-width: 70px;
            border-radius: 100px;
            background-color: ${color("common.ui2", 0.2)};
            vertical-align: center;
            text-align: center;
            padding: 6px 8px;
            width: fit-content;
            margin-bottom: 8px;
        `,
        mergeCss: true,
        ...fontConfig.xxSmallSingleLine,
        weight: fontConfig.weight.medium,
        color: "common.ui2",
    },
};

const styles = locationContactFieldStyles;
const LocationResultCard: React.FC<LocationResultCardProps> = ({
    location,
    distanceUnitOfMeasure,
    onLocationClick,
}) => {
    const isStoneAgeOffice = location.properties?.locationType?.includes(STONEAGE_OFFICE_LOCATION_TYPE);

    return (
        <StyledWrapper
            {...styles.searchResultStyledWrapper}
            {...{ isStoneAge: isStoneAgeOffice }}
            data-test-selector="locationSearchForm_location"
            data-test-key={`${location.id}`}
        >
            <DistanceDisplay
                {...styles.locationDistanceDisplayText}
                distance={location.distance}
                overrideTitle={translate("{0} {1} away")}
                unitOfMeasure={distanceUnitOfMeasure}
            />
            <Link
                {...styles.locationNameLink}
                onClick={event => onLocationClick(event, location)}
                data-test-selector="findLocationModal_locationSelect"
            >
                {location.name}
            </Link>
            <StyledWrapper {...styles.locationTypesStylesWrapper}>
                <AddressInfoCondensedDisplay {...styles.locationAddressInfoCondensed} {...location} />
            </StyledWrapper>
            {location.phone && (
                <LocationContactField
                    label={translate("Phone")}
                    href={`tel:${location.phone}`}
                    value={location.phone}
                />
            )}
            {location.properties?.email && (
                <LocationContactField
                    label={translate("Email")}
                    href={`mailto:${location.properties.email}`}
                    value={location.properties.email}
                />
            )}
            <LocationTypeTags locationType={location.properties?.locationType} />
        </StyledWrapper>
    );
};

export default LocationResultCard;
